import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ChevronRight,
  MapPin,
  Star,
} from 'lucide-react';

import { partners } from '../data/mock';
import { projects } from '../data/projects';
import { useT } from '../i18n/LanguageContext';

const heroBg =
  'https://images.unsplash.com/photo-1509391366360-2e959784a276?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxzb2xhciUyMHBhbmVsc3xlbnwwfHx8fDE3ODQ4MTY4NDJ8MA&ixlib=rb-4.1.0&q=85';

const getLocalizedValue = (value, lang, fallback = '') => {
  if (!value) return fallback;

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'object') {
    return (
      value?.[lang] ||
      value?.fr ||
      value?.en ||
      value?.ar ||
      Object.values(value)[0] ||
      fallback
    );
  }

  return fallback;
};


/* TAG TRANSLATIONS */
const tagTranslations = {
  en: {
    'Borne de recharge': 'Charging Station',
    'Déploiement': 'Deployment',
    'Infrastructure': 'Infrastructure',
    'Industrie': 'Industry',
    'École': 'Education',
    'Tertiaire': 'Commercial',
    'Hôtellerie': 'Hospitality',
    'Rénovation': 'Renovation',
    'IRVE': 'EV Charging',
    'Photovoltaïque': 'Photovoltaic',
    'Énergie solaire': 'Solar Energy',
    'Toiture': 'Rooftop',
    'Ombrières': 'Solar Canopies',
    'Agricole': 'Agriculture',
    'Parking': 'Parking',
  },

  ar: {
    'Borne de recharge': 'محطة شحن',
    'Déploiement': 'النشر',
    'Infrastructure': 'البنية التحتية',
    'Industrie': 'الصناعة',
    'École': 'التعليم',
    'Tertiaire': 'القطاع التجاري',
    'Hôtellerie': 'الضيافة والفنادق',
    'Rénovation': 'إعادة التأهيل',
    'IRVE': 'شحن المركبات الكهربائية',
    'Photovoltaïque': 'الطاقة الشمسية الكهروضوئية',
    'Énergie solaire': 'الطاقة الشمسية',
    'Toiture': 'الأسطح',
    'Ombrières': 'المظلات الشمسية',
    'Agricole': 'القطاع الزراعي',
    'Parking': 'مواقف السيارات',
  },
};

const getLocalizedTag = (tag, lang) => {
  return tagTranslations?.[lang]?.[tag] || tag;
};


const getLocalizedService = (project, t, lang) => {
  const serviceSlug = project.serviceSlug;

  const translatedService =
    t?.services?.[serviceSlug]?.title ||
    t?.services?.[serviceSlug]?.name ||
    t?.servicesPage?.[serviceSlug] ||
    '';

  if (translatedService) {
    return translatedService;
  }

  return (
    getLocalizedValue(project.serviceLabel, lang) ||
    serviceSlug ||
    'Projet'
  );
};

const ProjectCard = ({
  p,
  featured,
  featuredLabel,
}) => (
  <article className="group rounded-2xl overflow-hidden border border-slate-200 bg-white hover:shadow-xl hover:-translate-y-1 transition-all duration-300">

    <div className="relative aspect-[4/3] overflow-hidden">

      <img
        src={p.image}
        alt={p.name}
        loading="lazy"
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
      />

      <span className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-[#0b3b8c] text-white text-[10px] tracking-[0.15em] font-bold flex items-center gap-1">

        {featured ? (
          <>
            <Star className="w-3 h-3" />
            {featuredLabel}
          </>
        ) : (
          p.category
        )}

      </span>

    </div>

    <div className="p-5">

      <h3 className="text-[#0b1c3a] font-bold text-base leading-snug">
        {p.name}
      </h3>

      <div className="mt-1 text-slate-500 text-xs flex items-center gap-1">
        <MapPin className="w-3 h-3" />
        {p.location}
        {p.year && <span>· {p.year}</span>}
      </div>

      {p.description && (
        <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-3">
          {p.description}
        </p>
      )}

      <div className="mt-4 flex flex-wrap gap-2">

        {p.tags?.map((tag, index) => (
          <span
            key={`${tag}-${index}`}
            className="px-2.5 py-1 rounded-full bg-[#0b3b8c]/8 text-[#0b3b8c] text-[11px] font-semibold"
          >
            {getLocalizedTag(tag, p.lang)}
          </span>
        ))}

      </div>

    </div>

  </article>
);

const References = () => {
  const { lang, t } = useT();

  const currentLang = ['fr', 'en', 'ar'].includes(lang)
    ? lang
    : 'fr';

  const isArabic = currentLang === 'ar';

  const [activeCat, setActiveCat] = useState('all');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /*
   * Convert project data into language-aware data.
   */
  const localizedProjects = useMemo(() => {
    return projects.map((project) => {

      const localizedTitle =
        getLocalizedValue(project.title, currentLang) ||
        project.title ||
        '';

      const localizedLocation =
        getLocalizedValue(project.location, currentLang) ||
        project.location ||
        '';

      const localizedDescription =
        getLocalizedValue(
          project.description,
          currentLang
        );

      const localizedLongDescription =
        getLocalizedValue(
          project.longDescription,
          currentLang
        );

      const localizedCategory =
        getLocalizedService(
          project,
          t,
          currentLang
        );

      return {
        ...project,

        lang: currentLang,

        name: localizedTitle,

        location: localizedLocation,

        category: localizedCategory,

        description: localizedDescription,

        longDescription: localizedLongDescription,

        image: project.image,

        tags: Array.isArray(project.tags)
          ? project.tags
          : [],
      };
    });
  }, [currentLang, t]);

  /*
   * First 3 projects are featured.
   */
  const featuredProjects = useMemo(() => {
    return localizedProjects.slice(0, 3);
  }, [localizedProjects]);

  /*
   * Categories use stable service slugs internally.
   * Labels change with language.
   */
  const categories = useMemo(() => {

    const categoryMap = new Map();

    localizedProjects.forEach((project) => {

      const key =
        project.serviceSlug ||
        project.category;

      if (!categoryMap.has(key)) {
        categoryMap.set(
          key,
          project.category
        );
      }

    });

    return [
      {
        key: 'all',
        label:
          t?.common?.allProjects ||
          'All projects',
      },
      ...Array.from(
        categoryMap.entries()
      ).map(([key, label]) => ({
        key,
        label,
      })),
    ];

  }, [localizedProjects, t]);

  /*
   * Filter projects.
   */
  const filtered = useMemo(() => {

    if (activeCat === 'all') {
      return localizedProjects;
    }

    return localizedProjects.filter(
      (project) =>
        (project.serviceSlug ||
          project.category) === activeCat
    );

  }, [activeCat, localizedProjects]);

  /*
   * Reset category after language changes.
   */
  useEffect(() => {

    if (
      activeCat !== 'all' &&
      !categories.some(
        (category) =>
          category.key === activeCat
      )
    ) {
      setActiveCat('all');
    }

  }, [categories, activeCat]);

  const references = t.references || {};

  return (
    <main
      className="bg-white"
      dir={isArabic ? 'rtl' : 'ltr'}
    >

      {/* HERO */}

      <section className="relative pt-40 pb-24 md:pt-48 md:pb-28 overflow-hidden">

        <img
          src={heroBg}
          alt={references.pill || 'GPE References'}
          loading="eager"
          className="absolute inset-0 w-full h-full object-cover"
        />

        <div className="absolute inset-0 bg-gradient-to-b from-[#0b1c3a]/95 via-[#0b1c3a]/80 to-[#0b1c3a]/95" />

        <div className="absolute right-[-10%] top-1/2 -translate-y-1/2 w-[720px] h-[720px] rounded-full bg-[#0b3b8c] blur-[140px] opacity-40 pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">

          <span className="inline-block px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white/90 text-[11px] tracking-[0.25em] font-semibold">
            {references.pill}
          </span>

          <h1 className="mt-6 text-white font-black leading-[1.05] tracking-tight text-5xl md:text-6xl lg:text-7xl max-w-4xl">

            {references.titleA}{' '}

            <span className="italic font-serif text-[#f5c518]">
              {references.titleAccent}
            </span>

          </h1>

          <p className="mt-6 text-white/85 text-base md:text-lg max-w-2xl leading-relaxed">
            {references.subtitle}
          </p>

          <nav
            aria-label="breadcrumb"
            className="mt-8 flex items-center gap-2 text-white/70 text-sm"
          >

            <Link
              to="/"
              className="hover:text-white transition-colors"
            >
              {t?.nav?.home || 'Home'}
            </Link>

            <ChevronRight className="w-3.5 h-3.5" />

            <span className="text-[#f5c518] font-semibold">
              {t?.nav?.references || references.pill}
            </span>

          </nav>

        </div>

        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-white" />

      </section>


      {/* FEATURED PROJECTS */}

      <section className="pb-16">

        <div className="max-w-7xl mx-auto px-6 lg:px-10">

          <div className="flex items-end justify-between gap-4 flex-wrap">

            <div>

              <span className="inline-block px-3 py-1 rounded-full border border-slate-200 bg-slate-50 text-slate-600 text-[11px] tracking-[0.25em] font-semibold">
                {references.featuredBadge}
              </span>

              <h2 className="mt-4 text-3xl md:text-4xl font-black text-[#0b1c3a] tracking-tight leading-tight">

                {references.featuredTitleA}{' '}

                <span className="italic font-serif text-[#0b3b8c]">
                  {references.featuredTitleAccent}
                </span>

                .

              </h2>

              <p className="mt-3 text-slate-600 max-w-2xl">
                {references.featuredSub}
              </p>

            </div>

          </div>

          <div className="mt-10 grid md:grid-cols-3 gap-6">

            {featuredProjects.map((project) => (
              <ProjectCard
                key={project.id}
                p={project}
                featured
                featuredLabel={(
                  references.featuredBadge ||
                  'FEATURED PROJECTS'
                ).replace('+ ', '')}
              />
            ))}

          </div>

        </div>

      </section>


      {/* FILTER + FULL GRID */}

      <section className="py-16 bg-[#f7f8fb] border-y border-slate-200">

        <div className="max-w-7xl mx-auto px-6 lg:px-10">

          <div className="flex flex-wrap gap-2">

            {categories.map((category) => (

              <button
                key={category.key}
                onClick={() =>
                  setActiveCat(category.key)
                }
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  activeCat === category.key
                    ? 'bg-[#0b3b8c] text-white shadow-md shadow-blue-900/20'
                    : 'bg-white text-slate-700 border border-slate-200 hover:border-[#0b3b8c]/30'
                }`}
              >
                {category.label}
              </button>

            ))}

          </div>


          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">

            {filtered.map((project) => (

              <ProjectCard
                key={project.id}
                p={project}
                featured={false}
              />

            ))}

          </div>

        </div>

      </section>


      {/* CLIENT LOGOS */}

      <section className="py-24">

        <div className="max-w-7xl mx-auto px-6 lg:px-10">

          <div className="text-center max-w-3xl mx-auto">

            <div className="text-[#0b3b8c] text-[12px] tracking-[0.35em] font-bold">
              {references.partnersTagline}
            </div>

            <h2 className="mt-4 text-3xl md:text-5xl font-black text-[#0b1c3a] tracking-tight">

              {references.partnersTitleA}{' '}

              <span className="italic font-serif text-[#0b3b8c]">
                {references.partnersTitleAccent}
              </span>

              …

            </h2>

            <p className="mt-4 text-slate-600">
              {references.partnersSub}
            </p>

          </div>


          <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">

            {partners.map((p) => (

              <div
                key={p.name}
                className="aspect-[3/2] bg-white rounded-2xl border border-slate-200 flex items-center justify-center px-4 py-3 hover:shadow-md hover:border-[#0b3b8c]/30 transition-all"
              >

                <img
                  src={p.logo}
                  alt={p.name}
                  loading="lazy"
                  className="max-h-full max-w-full object-contain"
                />

              </div>

            ))}

          </div>

        </div>

      </section>


      {/* CTA */}

      <section className="pb-24">

        <div className="max-w-7xl mx-auto px-6 lg:px-10">

          <div className="relative rounded-3xl overflow-hidden bg-[#0b1c3a] px-8 py-14 md:px-16 md:py-16 text-white text-center">

            <div className="absolute inset-0 opacity-25 bg-[radial-gradient(circle_at_15%_50%,#0b3b8c_0%,transparent_45%),radial-gradient(circle_at_85%_50%,#f5c518_0%,transparent_35%)]" />

            <div className="relative">

              <h2 className="text-3xl md:text-4xl font-black tracking-tight leading-tight">
                {references.ctaTitle}
              </h2>

              <p className="mt-4 text-white/80 max-w-2xl mx-auto">
                {references.ctaSub}
              </p>

              <Link
                to="/contact"
                className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#f5c518] text-[#0b1c3a] font-bold text-sm hover:bg-yellow-300 transition-colors"
              >
                {t?.common?.contactUs || 'Contact us'}
                <ArrowRight className="w-4 h-4" />
              </Link>

            </div>

          </div>

        </div>

      </section>

    </main>
  );
};

export default References;
